import React, { ReactElement, useState } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  FilterList,
  Add,
  FileDownload,
  Delete,
  Sort,
  FileUpload,
  Search,
} from '@mui/icons-material';
import SideDrawer from '../atoms/Drawer';
import { SearchBar } from '../atoms/SearchBar';
import { IconButton } from '../atoms/IconButton';
import { DialogBoxComp } from '../atoms/DialogBox';
import {
  IconPrimaryButton,
  IconTextButton,
  SecondaryButton,
} from '../atoms/Button';
import { Backdoor } from './Backdoor';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import { PageHeaderBar } from '../layout/PageHeaderBar';
import {
  APP_SHELL_HEADER_CONTROL_HEIGHT,
  appShellHeaderTitleSx,
} from '../layout/layoutConstants';

type FilterAction =
  | 'SEARCH'
  | 'FILTER'
  | 'SORT'
  | 'ADD'
  | 'EXPORT'
  | 'DELETE'
  | 'UPLOAD';

interface SmartFilterProps {
  title?: string;
  enableBack?: boolean;

  filterElement?: React.ReactNode;
  filterHeader?: string;
  addElement?: React.ReactNode;
  uploadElement?: React.ReactNode;
  addHeader?: string;
  addbuttontitle?: string;
  uploadHeader?: string;
  onSaved?: any;
  onFiltered?: any;
  onSearch?: (search: string) => void;
  createFilter?: FilterAction[];
  filterdrawerSize?: 'sm' | 'md' | 'lg';
  addDrawerSize?: 'sm' | 'md' | 'lg';
  onSort?: () => void;
  onNavigate?: () => void;
  onUpload?: () => void;
  extraActions?: React.ReactNode;
}
export const SmartFilter = ({
  title,
  filterElement,
  addElement,
  filterHeader,
  addHeader,
  uploadHeader,
  uploadElement,
  onUpload,
  onSaved,
  onSearch,
  onSort,
  onFiltered,
  createFilter,
  filterdrawerSize = 'sm',
  addDrawerSize = 'sm',
  onNavigate,
  addbuttontitle,
  enableBack = false,
  extraActions,
}: SmartFilterProps) => {
  const { canCreate, canDelete } = useAdminPermission();
  const [draweropen, setDraweropen] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [renderComponent, setRenderComponent] = useState<React.ReactElement>();
  const [renderHeader, setRenderHeader] = useState<string>();
  const [drawerSize, setDrawerSize] = useState<'sm' | 'md' | 'lg'>('sm');

  const handleDrawerClose = () => {
    setDraweropen(false);
  };

  const handleAddClick = () => {
    if (addElement && addHeader) {
      handleDrawerOpen(
        addElement as React.ReactElement,
        addHeader,
        addDrawerSize,
      );
    } else if (onNavigate) {
      onNavigate(); // Call the navigation function if provided
    }
  };

  const handleFilterClick = () => {
    if (filterElement && filterHeader) {
      handleDrawerOpen(
        filterElement as React.ReactElement,
        filterHeader,
        filterdrawerSize,
      );
    }
  };

  const handleUpload = () => {
    setDialogOpen(true);
  };

  const handleDrawerOpen = (
    component: any,
    header: string,
    size: 'sm' | 'md' | 'lg',
  ) => {
    setDrawerSize(size);
    setRenderComponent(component);
    setRenderHeader(header);
    setDraweropen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  return (
    <>
      {/* Search & Filter Bar */}

      <PageHeaderBar showDivider>
        <Box flex={1} minWidth={0} display="flex" alignItems="center">
          {enableBack ? (
            <Backdoor title={title || 'N/A'} />
          ) : (
            <Typography sx={appShellHeaderTitleSx}>
              {title || 'N/A'}
            </Typography>
          )}
        </Box>
        <Box
          flex={1}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          minWidth={0}
        >
          {createFilter?.includes('FILTER') && (
            <IconTextButton
              title="Filter"
              icon={<FilterList />}
              onClick={handleFilterClick}
              height={APP_SHELL_HEADER_CONTROL_HEIGHT}
            />
          )}

          {createFilter?.includes('SORT') && (
            <IconButton onClick={onSort} text={'Sort'} startIcon={<Sort />} />
          )}
          {/* Action Buttons */}

          {createFilter?.includes('SEARCH') && (
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              onChange={(e) => onSearch?.(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: '#aaa' }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  backgroundColor: '#333',
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#555',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#777',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#7B1FA2',
                  },
                  height: APP_SHELL_HEADER_CONTROL_HEIGHT,
                },
              }}
              sx={{ width: 280 }}
            />
          )}
          {createFilter?.includes('ADD') && canCreate() && (
            <IconPrimaryButton
              title={addbuttontitle || 'Add'}
              icon={<Add />}
              onClick={handleAddClick}
              height={APP_SHELL_HEADER_CONTROL_HEIGHT}
              size="small"
            />
          )}
          {extraActions}
          {createFilter?.includes('EXPORT') && (
            <Grid item xs={4}>
              <Button
                // onClick={() => handleDrawerOpen(ExportComponent)}
                variant="contained"
                startIcon={<FileDownload />}
                fullWidth
              >
                Export
              </Button>
            </Grid>
          )}
          {createFilter?.includes('UPLOAD') && (
            <Grid item xs={4}>
              <Button
                onClick={handleUpload}
                variant="contained"
                startIcon={<FileUpload />}
                fullWidth
              >
                Upload
              </Button>
            </Grid>
          )}
          {createFilter?.includes('DELETE') && canDelete() && (
            <Grid item xs={4}>
              <Button
                // onClick={() => handleDrawerOpen(DeleteComponent)}
                variant="contained"
                startIcon={<Delete />}
                fullWidth
              >
                Delete
              </Button>
            </Grid>
          )}
        </Box>
      </PageHeaderBar>

      <SideDrawer
        open={draweropen}
        onClose={handleDrawerClose}
        renderComponent={
          renderComponent
            ? React.cloneElement(renderComponent, {
                onSaved,
                onFiltered,
                handleDrawerClose,
              })
            : null
        }
        renderHeader={renderHeader}
        size={drawerSize}
      />

      <DialogBoxComp
        open={dialogOpen}
        title={uploadHeader as string}
        contentElement={
          uploadElement
            ? React.cloneElement(uploadElement as ReactElement, {
                onUpload,
                handleDialogClose,
              })
            : null
        }
        handleClose={handleDialogClose}
        handleConfirm={onUpload as () => void}
      />
    </>
  );
};
